import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  Input,
  Text,
  Box,
  HStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Plus, Edit, X, Gamepad2, Loader2, AlertCircle, Power, Trash2, CalendarClock, Trophy } from 'lucide-react';
import { getGames, createGame, updateGame, toggleGameStatus, deleteGame } from '../actions/gameActions';
import styles from '../styles/GameManagement.module.css';
import { useAppToast } from '../hooks/useAppToast';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';
import { isSuperAdminRole } from '../utils/auth';
import ManualResultModal from '../components/ManualResultModal';

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function clampPercent(value, fallback) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.min(100, n);
}
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const GAME_TYPES = [
  { value: 'single', label: 'Single (0-9)' },
  { value: 'double', label: 'Double (00-99)' },
  { value: 'triple', label: 'Triple (000-999)' },
  { value: 'joker', label: 'Joker' },
  { value: 'card', label: 'Card' },
  { value: 'loot', label: 'Loot' },
];

const splitTime = (value) => {
  const [hour = '', minute = ''] = String(value || '').split(':');
  return { hour, minute };
};

// Complete times in saved (sorted) order, unfinished rows kept at the bottom.
const sortTimes = (times) => [
  ...times.filter((t) => HHMM_RE.test(t)).sort(),
  ...times.filter((t) => !HHMM_RE.test(t)),
];

const todayDrawsMessage = (summary) => {
  if (!summary) return '';
  if (summary.error) return ` Today's draws were not updated: ${summary.error}`;
  const parts = [];
  if (summary.created?.length) parts.push(`${summary.created.length} added`);
  if (summary.removed?.length) parts.push(`${summary.removed.length} removed`);
  if (summary.kept?.length) parts.push(`${summary.kept.length} kept (tickets booked)`);
  return parts.length ? ` Today's draws: ${parts.join(', ')}.` : " Today's draws already match the schedule.";
};

const GameManagement = () => {
  const dispatch = useDispatch();
  const { showSuccess, showError, showApiError } = useAppToast();
  const { canShowField } = useFieldPrivilege();
  const { games, loading, error } = useSelector(state => state.game);
  const { user } = useSelector(state => state.auth);
  const isSuperAdmin = isSuperAdminRole(user?.role);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newGame, setNewGame] = useState({
    gameNumber: "",
    gameType: '',
    gameCode: '',
    name: '',
    bet: 1,
    win: '',
    interval: 120,
    drawCalc: '',
    totalCalc: '',
    bonus: 0,
    is_active: true,
    autoBlock: true,
    checkHighN2P: true,
    forceX: false,
    allowRepeatDigits: true,
    startTime: '',
    endTime: '',
    manual_draw: false,
    manual_times: [],
    today_draws: false,
    no_more_bet: 0,
    single_max_bet: 0,
    patti_max_bet: 0,
    allow_adv_booking: false,
    patti_win: 100,
    high_bonus: 1,
    auto_win_cap_pct: 80,
    auto_win_frequency_pct: 50,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentGameId, setCurrentGameId] = useState(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [actionResultGame, setActionResultGame] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const deleteModalSize = useBreakpointValue({ base: 'full', sm: 'md' }) ?? 'md';

  useEffect(() => {
    dispatch(getGames());
  }, [dispatch]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setNewGame(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const handleNumberInputChange = useCallback((name, value) => {
    setNewGame(prev => ({ ...prev, [name]: value || 0 }));
  }, []);

  const handleManualDrawToggle = useCallback((e) => {
    const { checked } = e.target;
    setNewGame(prev => ({
      ...prev,
      manual_draw: checked,
      manual_times: checked && prev.manual_times.length === 0 ? [''] : prev.manual_times,
    }));
  }, []);

  const handleManualTimeChange = useCallback((index, part, value) => {
    setNewGame(prev => {
      const manual_times = [...prev.manual_times];
      const current = splitTime(manual_times[index]);
      const next = { ...current, [part]: value };
      manual_times[index] = next.hour || next.minute ? `${next.hour}:${next.minute}` : '';
      return {
        ...prev,
        manual_times: HHMM_RE.test(manual_times[index]) ? sortTimes(manual_times) : manual_times,
      };
    });
  }, []);

  const addManualTime = useCallback(() => {
    setNewGame(prev => ({ ...prev, manual_times: [...prev.manual_times, ''] }));
  }, []);

  const removeManualTime = useCallback((index) => {
    setNewGame(prev => ({ ...prev, manual_times: prev.manual_times.filter((_, i) => i !== index) }));
  }, []);

  const resetForm = () => {
    setIsEditMode(false);
    setCurrentGameId(null);
    setNewGame({
      gameCode: '',
      gameNumber: "",
      gameType: '',
      name: '',
      bet: 1,
      win: '',
      interval: 120,
      drawCalc: '',
      totalCalc: '',
      bonus: 0,
      autoBlock: true,
      checkHighN2P: true,
      forceX: false,
      allowRepeatDigits: true,
      is_active: true,
      startTime: '',
      endTime: '',
      manual_draw: false,
      manual_times: [],
      today_draws: false,
      no_more_bet: 0,
      single_max_bet: 0,
      patti_max_bet: 0,
      allow_adv_booking: false,
      patti_win: 100,
      high_bonus: 1,
      auto_win_cap_pct: 80,
      auto_win_frequency_pct: 50,
    });
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      showError("You don't have access to modify games.");
      return;
    }
    const manualDraw = !!newGame.manual_draw;
    const manualTimes = manualDraw ? newGame.manual_times.map((t) => String(t || '').trim()) : newGame.manual_times;
    if (manualDraw) {
      if (manualTimes.length === 0) {
        showError('Add at least one draw time for a manual draw game.');
        return;
      }
      if (manualTimes.some((t) => !HHMM_RE.test(t))) {
        showError('Select both hour and minute for every draw time.');
        return;
      }
      if (new Set(manualTimes).size !== manualTimes.length) {
        showError('Each manual draw time must be different.');
        return;
      }
    }
    const payload = {
      ...newGame,
      bonus: Number(newGame.bonus) || 0,
      no_more_bet: Number(newGame.no_more_bet) || 0,
      single_max_bet: Number(newGame.single_max_bet) || 0,
      patti_max_bet: Number(newGame.patti_max_bet) || 0,
      allow_adv_booking: !!newGame.allow_adv_booking,
      patti_win: Number(newGame.patti_win) > 0 ? Number(newGame.patti_win) : 100,
      high_bonus: Math.max(1, Math.floor(Number(newGame.high_bonus)) || 1),
      auto_win_cap_pct: clampPercent(newGame.auto_win_cap_pct, 80),
      auto_win_frequency_pct: clampPercent(newGame.auto_win_frequency_pct, 50),
      manual_draw: manualDraw,
      manual_times: manualDraw ? [...manualTimes].sort() : manualTimes.filter((t) => HHMM_RE.test(t)),
      today_draws: manualDraw && !!newGame.today_draws,
    };
    try {
      if (isEditMode) {
        const original = games.find((g) => g._id === currentGameId) || {};
        const scheduleChanged =
          !!original.manual_draw !== manualDraw ||
          (manualDraw && (original.manual_times || []).join(',') !== payload.manual_times.join(','));
        const res = await dispatch(updateGame({
          gameId: currentGameId,
          updateData: payload
        }));
        if (payload.today_draws) {
          showSuccess(`Game updated successfully.${todayDrawsMessage(res?.todayDraws)}`);
        } else {
          showSuccess(scheduleChanged
            ? "Game updated successfully. Draw schedule changes apply from tomorrow's draws."
            : "Game updated successfully.");
        }
      } else {
        const nextDrawNumber = (games.length > 0 ? Math.max(...games.map(g => g.drawNumber || 0)) : 0) + 1;
        const res = await dispatch(createGame({ ...payload, drawNumber: nextDrawNumber }));
        showSuccess(`Game created successfully.${payload.today_draws ? todayDrawsMessage(res?.todayDraws) : ''}`);
      }
      await dispatch(getGames());
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      showApiError(err, 'Error saving game');
    }
  }, [dispatch, isEditMode, newGame, currentGameId, games, isSuperAdmin]);

  const handleToggleStatus = useCallback((gameId) => {
    if (!isSuperAdmin) {
      showError("You don't have access to modify games.");
      return;
    }
    dispatch(toggleGameStatus(gameId))
      .then(() => {
        showSuccess("Game status updated successfully.");
        return dispatch(getGames());
      })
      .catch((err) => {
        showApiError(err, 'Error updating game status');
      });
  }, [dispatch, isSuperAdmin, showError, showSuccess, showApiError]);

  const handleDeleteGame = useCallback((game) => {
    if (!isSuperAdmin) {
      showError("You don't have access to delete games.");
      return;
    }
    setDeleteTarget(game);
    setDeleteConfirmText('');
    setDeleteInfo(null);
    if (game?.manual_draw) {
      axios
        .post(`${process.env.REACT_APP_API_URL_ADMIN}`, { apiName: 'draw', command: 'getManualDraws', gameId: game._id })
        .then((res) => {
          const draws = res.data?.data?.draws || [];
          setDeleteInfo({
            draws: draws.length,
            tickets: draws.reduce((sum, d) => sum + (Number(d.tickets) || 0), 0),
            pending: draws.filter((d) => d.result == null).length,
          });
        })
        .catch(() => setDeleteInfo(null));
    }
  }, [isSuperAdmin, showError]);

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteConfirmText('');
    setDeleteInfo(null);
  };

  const deleteCode = String(deleteTarget?.gameCode || '').trim();
  const deleteConfirmed =
    Boolean(deleteCode) && deleteConfirmText.trim().toUpperCase() === deleteCode.toUpperCase();

  const confirmDeleteGame = async () => {
    if (!deleteTarget || !deleteConfirmed) return;
    setDeleting(true);
    try {
      await dispatch(deleteGame(deleteTarget._id));
      showSuccess(`Game "${deleteTarget.name || deleteCode}" deleted.`);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      setDeleteInfo(null);
      await dispatch(getGames());
    } catch (err) {
      showApiError(err, 'Error deleting game');
    } finally {
      setDeleting(false);
    }
  };

  const openResultForGame = useCallback((game) => {
    setActionResultGame(game);
    setResultModalOpen(true);
  }, []);

  const handleEdit = useCallback((game) => {
    if (!isSuperAdmin) {
      showError("You don't have access to modify games.");
      return;
    }
    setIsEditMode(true);
    setCurrentGameId(game._id);
    setNewGame({
      _id: game._id,
      gameCode: game.gameCode || '',
      gameNumber: game.gameNumber || '',
      name: game.name || '',
      gameType: game.gameType || '',
      bet: game.bet || 1,
      win: game.win ?? '',
      interval: game.interval || 120,
      allowRepeatDigits: game.allowRepeatDigits ?? true,
      autoBlock: game.autoBlock ?? true,
      drawCalc: game.drawCalc ?? '',
      totalCalc: game.totalCalc ?? '',
      bonus: game.bonus ?? 0,
      checkHighN2P: game.checkHighN2P ?? true,
      forceX: game.forceX ?? false,
      is_active: game.is_active ?? true,
      startTime: game.startTime ? game.startTime.slice(0, 5) : '',
      endTime: game.endTime ? game.endTime.slice(0, 5) : '',
      manual_draw: game.manual_draw ?? false,
      manual_times: Array.isArray(game.manual_times) ? sortTimes(game.manual_times) : [],
      today_draws: Boolean(game.today_draws),
      no_more_bet: game.no_more_bet ?? 0,
      single_max_bet: game.single_max_bet ?? 0,
      patti_max_bet: game.patti_max_bet ?? 0,
      allow_adv_booking: Boolean(game.allow_adv_booking),
      patti_win: game.patti_win ?? 100,
      high_bonus: game.high_bonus ?? 1,
      auto_win_cap_pct: game.auto_win_cap_pct ?? 80,
      auto_win_frequency_pct: game.auto_win_frequency_pct ?? 50,
    });
    setIsModalOpen(true);
  }, [isSuperAdmin]);

  const handleAddGame = () => {
    if (!isSuperAdmin) {
      showError("You don't have access to modify games.");
      return;
    }
    resetForm();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setResultModalOpen(false);
    resetForm();
  };

  const savedManualGame = isEditMode
    ? games.find((g) => g._id === currentGameId && g.manual_draw) || null
    : null;
  const resultGame = actionResultGame || savedManualGame;

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.spinner} size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorAlert}>
        <AlertCircle size={20} />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className={styles.gameManagement}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Gamepad2 size={24} className={styles.headerIcon} />
          <h1 className={styles.title}>Game Master</h1>
        </div>
        {isSuperAdmin && (
          <button className={styles.addButton} onClick={handleAddGame}>
            <Plus size={20} />
            <span>Add Game</span>
          </button>
        )}
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {canShowField('game', 'drawNumber', 'list') && <th>Draw Number</th>}
              {canShowField('game', 'gameCode', 'list') && <th>Game Code</th>}
              {canShowField('game', 'gameNumber', 'list') && <th>Game Number</th>}
              {canShowField('game', 'name', 'list') && <th>Draw Name</th>}
              {canShowField('game', 'bet', 'list') && <th>Bet</th>}
              {canShowField('game', 'win', 'list') && <th>Win</th>}
              {canShowField('game', 'interval', 'list') && <th>Interval</th>}
              {canShowField('game', 'drawCalc', 'list') && <th>Draw Calc</th>}
              {canShowField('game', 'totalCalc', 'list') && <th>Total Calc</th>}
              {canShowField('game', 'bonus', 'list') && <th>Bonus</th>}
              {canShowField('game', 'autoBlock', 'list') && <th>Auto Block</th>}
              {canShowField('game', 'checkHighN2P', 'list') && <th>Check High N2P</th>}
              {canShowField('game', 'forceX', 'list') && <th>ForceX</th>}
              {canShowField('game', 'is_active', 'list') && <th>Status</th>}
              {isSuperAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {games.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    (isSuperAdmin ? 1 : 0) +
                    [
                      'drawNumber', 'gameCode', 'gameNumber', 'name', 'bet', 'win', 'interval',
                      'drawCalc', 'totalCalc', 'bonus', 'autoBlock', 'checkHighN2P', 'forceX', 'is_active',
                    ].filter((f) => canShowField('game', f, 'list')).length
                  }
                  className={styles.emptyState}
                >
                  No games found. Click "Add Game" to create one.
                </td>
              </tr>
            ) : (
              games.map((game) => (
                <tr key={game._id}>
                  {canShowField('game', 'drawNumber', 'list') && <td>{game.drawNumber || '-'}</td>}
                  {canShowField('game', 'gameCode', 'list') && <td>{game.gameCode || '-'}</td>}
                  {canShowField('game', 'gameNumber', 'list') && <td>{game.gameNumber || '-'}</td>}
                  {canShowField('game', 'name', 'list') && <td>{game.name || '-'}</td>}
                  {canShowField('game', 'bet', 'list') && <td>{game.bet || '-'}</td>}
                  {canShowField('game', 'win', 'list') && <td>{game.win || '-'}</td>}
                  {canShowField('game', 'interval', 'list') && (
                    <td title={game.manual_draw ? (game.manual_times || []).join(', ') : undefined}>
                      {game.manual_draw
                        ? `Manual (${(game.manual_times || []).length} times)`
                        : (game.interval || '-')}
                    </td>
                  )}
                  {canShowField('game', 'drawCalc', 'list') && <td>{game.drawCalc || '-'}</td>}
                  {canShowField('game', 'totalCalc', 'list') && <td>{game.totalCalc || '-'}</td>}
                  {canShowField('game', 'bonus', 'list') && <td>{game.bonus ?? 0}</td>}
                  {canShowField('game', 'autoBlock', 'list') && <td>{game.autoBlock ? 'Y' : 'N'}</td>}
                  {canShowField('game', 'checkHighN2P', 'list') && <td>{game.checkHighN2P ? 'Y' : 'N'}</td>}
                  {canShowField('game', 'forceX', 'list') && <td>{game.forceX ? 'Y' : 'N'}</td>}
                  {canShowField('game', 'is_active', 'list') && (
                    <td>
                      <span className={`${styles.statusBadge} ${game.is_active ? styles.statusActive : styles.statusInactive}`}>
                        {game.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  )}
                  {isSuperAdmin && (
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.iconButton}
                          onClick={() => handleToggleStatus(game._id)}
                          title={game.is_active ? 'Deactivate' : 'Activate'}
                          aria-label={game.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <Power size={18} />
                        </button>
                        <button
                          className={styles.iconButton}
                          onClick={() => handleEdit(game)}
                          title="Edit"
                          aria-label="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        {game.manual_draw && (
                          <button
                            className={`${styles.iconButton} ${styles.resultButton}`}
                            onClick={() => openResultForGame(game)}
                            title="Give Result"
                            aria-label="Give Result"
                          >
                            <Trophy size={18} />
                          </button>
                        )}
                        <button
                          className={`${styles.iconButton} ${styles.deleteButton}`}
                          onClick={() => handleDeleteGame(game)}
                          title="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {isEditMode ? 'Edit Game' : 'Add New Game'}
              </h2>
              <button className={styles.closeButton} onClick={handleCloseModal} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form className={styles.modalForm} onSubmit={handleSubmit}>
              <div className={styles.modalBody}>
                <div className={styles.formRow}>
                  {canShowField('game', 'gameCode', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Game Code <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="gameCode"
                      value={newGame.gameCode}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                    />
                  </div>
                  )}
                  {canShowField('game', 'gameNumber', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Game Number</label>
                    <input
                      type="text"
                      name="gameNumber"
                      value={newGame.gameNumber}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                  )}
                </div>

                <div className={styles.formRow}>
                  {canShowField('game', 'name', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Name <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={newGame.name}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                    />
                  </div>
                  )}
                  {canShowField('game', 'gameType', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Game Type <span className={styles.required}>*</span>
                    </label>
                    <select
                      name="gameType"
                      value={newGame.gameType}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                    >
                      <option value="">Select type</option>
                      {GAME_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  )}
                </div>

                <div className={styles.formRow}>
                  {canShowField('game', 'bet', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Bet <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="number"
                      name="bet"
                      min="1"
                      value={newGame.bet}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                    />
                  </div>
                  )}
                  {canShowField('game', 'win', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Win <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="number"
                      name="win"
                      step="any"
                      value={newGame.win}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                    />
                  </div>
                  )}
                </div>

                {canShowField('game', 'manual_draw', 'form') && (
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.switchLabel}>
                      <input
                        type="checkbox"
                        name="manual_draw"
                        checked={!!newGame.manual_draw}
                        onChange={handleManualDrawToggle}
                        className={styles.switch}
                      />
                      <span className={styles.switchText}>Manual Draw</span>
                    </label>
                  </div>
                </div>
                )}

                {newGame.manual_draw && (
                <div className={styles.formRow}>
                  {canShowField('game', 'patti_win', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label} htmlFor="game-patti_win">Patti Win</label>
                    <input
                      id="game-patti_win"
                      type="number"
                      name="patti_win"
                      min="1"
                      step="any"
                      value={newGame.patti_win}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                  )}
                  {canShowField('game', 'allow_adv_booking', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.switchLabel}>
                      <input
                        type="checkbox"
                        name="allow_adv_booking"
                        checked={!!newGame.allow_adv_booking}
                        onChange={handleInputChange}
                        className={styles.switch}
                      />
                      <span className={styles.switchText}>Allow Advance Booking</span>
                    </label>
                  </div>
                  )}
                </div>
                )}

                {newGame.manual_draw && canShowField('game', 'manual_times', 'form') && (
                <div className={styles.manualTimes}>
                  <div className={styles.manualTimesHeader}>
                    <span className={styles.label}>
                      Draw Times (24h) <span className={styles.required}>*</span>
                    </span>
                    <button type="button" className={styles.addTimeButton} onClick={addManualTime}>
                      <Plus size={14} />
                      <span>Add Time</span>
                    </button>
                  </div>
                  <p className={styles.manualTimesHint}>
                    Draws are created only at these times and results are never generated automatically.
                    Times are kept in order; changes apply from the next day&apos;s draws unless
                    &quot;Generate today&apos;s draws&quot; is on.
                  </p>
                  {newGame.manual_times.length === 0 && (
                    <p className={styles.manualTimesEmpty}>Add at least one draw time.</p>
                  )}
                  {newGame.manual_times.map((time, index) => {
                    const { hour, minute } = splitTime(time);
                    const isDuplicate = HHMM_RE.test(time) && newGame.manual_times.filter((t) => t === time).length > 1;
                    return (
                      <div
                        className={`${styles.manualTimeRow} ${isDuplicate ? styles.manualTimeDuplicate : ''}`}
                        key={index}
                        title={isDuplicate ? 'This time is listed more than once' : undefined}
                      >
                        <span className={styles.manualTimeIndex}>{index + 1}.</span>
                        <select
                          className={`${styles.input} ${styles.manualTimeSelect}`}
                          value={hour}
                          onChange={(e) => handleManualTimeChange(index, 'hour', e.target.value)}
                          aria-label={`Draw time ${index + 1} hour`}
                          required
                        >
                          <option value="">HH</option>
                          {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <span className={styles.manualTimeColon}>:</span>
                        <select
                          className={`${styles.input} ${styles.manualTimeSelect}`}
                          value={minute}
                          onChange={(e) => handleManualTimeChange(index, 'minute', e.target.value)}
                          aria-label={`Draw time ${index + 1} minute`}
                          required
                        >
                          <option value="">MM</option>
                          {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <button
                          type="button"
                          className={`${styles.iconButton} ${styles.deleteButton}`}
                          onClick={() => removeManualTime(index)}
                          title="Delete time"
                          aria-label={`Delete draw time ${index + 1}`}
                        >
                          <Trash2 size={16} />
                        </button>
                        {isDuplicate && <span className={styles.manualTimeDuplicateText}>Duplicate</span>}
                      </div>
                    );
                  })}

                  <div className={styles.manualActions}>
                    <label className={`${styles.manualActionCard} ${newGame.today_draws ? styles.manualActionCardOn : ''}`}>
                      <input
                        type="checkbox"
                        name="today_draws"
                        checked={!!newGame.today_draws}
                        onChange={handleInputChange}
                        className={styles.switch}
                      />
                      <span className={styles.manualActionBody}>
                        <span className={styles.manualActionTitle}>
                          <CalendarClock size={16} />
                          Generate today&apos;s draws
                        </span>
                        <span className={styles.manualActionHint}>
                          {newGame.today_draws
                            ? "On save, every time in today's schedule (past and upcoming) gets exactly one draw; existing draws are never duplicated. Removed times drop today's unresulted draw if no tickets are booked."
                            : "Off: today's draws stay as they are; schedule changes start tomorrow."}
                        </span>
                      </span>
                    </label>

                    <div className={styles.manualActionCard}>
                      <span className={styles.manualActionBody}>
                        <span className={styles.manualActionTitle}>
                          <Trophy size={16} />
                          Give result
                        </span>
                        <span className={styles.manualActionHint}>
                          {savedManualGame
                            ? "Pick one of today's draws (or an older one still waiting) and declare its result."
                            : 'Save the game as a manual draw game first.'}
                        </span>
                      </span>
                      <button
                        type="button"
                        className={styles.giveResultButton}
                        onClick={() => setResultModalOpen(true)}
                        disabled={!savedManualGame}
                      >
                        Give Result
                      </button>
                    </div>
                  </div>
                </div>
                )}

                <div className={styles.formRow}>
                  {!newGame.manual_draw && canShowField('game', 'interval', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Interval <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="number"
                      name="interval"
                      min="1"
                      value={newGame.interval}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                    />
                  </div>
                  )}
                  {canShowField('game', 'bonus', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Bonus</label>
                    <input
                      type="number"
                      name="bonus"
                      min="0"
                      value={newGame.bonus}
                      onChange={(e) => handleNumberInputChange('bonus', Number(e.target.value))}
                      className={styles.input}
                    />
                  </div>
                  )}
                  {!newGame.manual_draw && canShowField('game', 'high_bonus', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label} htmlFor="game-high_bonus">High Bonus</label>
                    <div className={styles.unitInput}>
                      <input
                        id="game-high_bonus"
                        type="number"
                        name="high_bonus"
                        min="1"
                        step="1"
                        inputMode="numeric"
                        value={newGame.high_bonus}
                        onChange={handleInputChange}
                        className={styles.input}
                        title="Highest bonus multiplier for a HIGH player's win (never above Bonus)"
                      />
                      <span className={styles.unitSuffix}>x</span>
                    </div>
                  </div>
                  )}
                  {!newGame.manual_draw && canShowField('game', 'auto_win_cap_pct', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label} htmlFor="game-auto_win_cap_pct">AUTO win cap</label>
                    <div className={styles.unitInput}>
                      <input
                        id="game-auto_win_cap_pct"
                        type="number"
                        name="auto_win_cap_pct"
                        min="1"
                        max="100"
                        step="1"
                        inputMode="numeric"
                        value={newGame.auto_win_cap_pct}
                        onChange={handleInputChange}
                        className={styles.input}
                        title="Largest share of the available win one AUTO draw may pay"
                      />
                      <span className={styles.unitSuffix}>%</span>
                    </div>
                  </div>
                  )}
                  {!newGame.manual_draw && canShowField('game', 'auto_win_frequency_pct', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label} htmlFor="game-auto_win_frequency_pct">AUTO win frequency</label>
                    <div className={styles.unitInput}>
                      <input
                        id="game-auto_win_frequency_pct"
                        type="number"
                        name="auto_win_frequency_pct"
                        min="1"
                        max="100"
                        step="1"
                        inputMode="numeric"
                        value={newGame.auto_win_frequency_pct}
                        onChange={handleInputChange}
                        className={styles.input}
                        title="Chance an AUTO draw with room pays a legal ticket instead of a blank"
                      />
                      <span className={styles.unitSuffix}>%</span>
                    </div>
                  </div>
                  )}
                </div>

                <div className={styles.formRow}>
                  {canShowField('game', 'drawCalc', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Draw Calc <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="number"
                      name="drawCalc"
                      step="any"
                      value={newGame.drawCalc}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                    />
                  </div>
                  )}
                  {canShowField('game', 'totalCalc', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Total Calc <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="number"
                      name="totalCalc"
                      step="any"
                      value={newGame.totalCalc}
                      onChange={handleInputChange}
                      className={styles.input}
                      required
                    />
                  </div>
                  )}
                </div>

                <div className={`${styles.formRow} ${styles.formRowThree}`}>
                  {[
                    { name: 'no_more_bet', label: 'No More Bet', unit: 'sec' },
                    { name: 'single_max_bet', label: 'Single Max Bet', unit: 'pts' },
                    { name: 'patti_max_bet', label: 'Patti Max Bet', unit: 'pts' },
                  ].filter(({ name }) => canShowField('game', name, 'form')).map(({ name, label, unit }) => (
                    <div className={styles.formGroup} key={name}>
                      <label className={styles.label} htmlFor={`game-${name}`}>{label}</label>
                      <div className={styles.unitInput}>
                        <input
                          id={`game-${name}`}
                          type="number"
                          name={name}
                          min="0"
                          step="1"
                          inputMode="numeric"
                          value={newGame[name]}
                          onChange={handleInputChange}
                          className={styles.input}
                        />
                        <span className={styles.unitSuffix}>{unit}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {!newGame.manual_draw && (
                <div className={styles.formRow}>
                  {canShowField('game', 'startTime', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Start Time</label>
                    <input
                      type="time"
                      name="startTime"
                      value={newGame.startTime}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                  )}
                  {canShowField('game', 'endTime', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>End Time</label>
                    <input
                      type="time"
                      name="endTime"
                      value={newGame.endTime}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                  )}
                </div>
                )}

                <div className={styles.formRow}>
                  {canShowField('game', 'autoBlock', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.switchLabel}>
                      <input
                        type="checkbox"
                        name="autoBlock"
                        checked={newGame.autoBlock}
                        onChange={handleInputChange}
                        className={styles.switch}
                      />
                      <span className={styles.switchText}>Auto Block</span>
                    </label>
                  </div>
                  )}
                  {canShowField('game', 'checkHighN2P', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.switchLabel}>
                      <input
                        type="checkbox"
                        name="checkHighN2P"
                        checked={newGame.checkHighN2P}
                        onChange={handleInputChange}
                        className={styles.switch}
                      />
                      <span className={styles.switchText}>Check High N2P</span>
                    </label>
                  </div>
                  )}
                </div>

                <div className={styles.formRow}>
                  {canShowField('game', 'forceX', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.switchLabel}>
                      <input
                        type="checkbox"
                        name="forceX"
                        checked={newGame.forceX}
                        onChange={handleInputChange}
                        className={styles.switch}
                      />
                      <span className={styles.switchText}>ForceX</span>
                    </label>
                  </div>
                  )}
                  {canShowField('game', 'allowRepeatDigits', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.switchLabel}>
                      <input
                        type="checkbox"
                        name="allowRepeatDigits"
                        checked={newGame.allowRepeatDigits}
                        onChange={handleInputChange}
                        className={styles.switch}
                      />
                      <span className={styles.switchText}>Allow Repeat Digits</span>
                    </label>
                  </div>
                  )}
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="submit" className={styles.submitButton}>
                  {isEditMode ? 'Update Game' : 'Add Game'}
                </button>
                <button type="button" className={styles.cancelButton} onClick={handleCloseModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ManualResultModal
        isOpen={resultModalOpen && Boolean(resultGame)}
        onClose={() => {
          setResultModalOpen(false);
          setActionResultGame(null);
        }}
        gameId={resultGame?._id}
        gameName={resultGame?.name}
      />

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={closeDeleteModal}
        size={deleteModalSize}
        isCentered={deleteModalSize !== 'full'}
        closeOnOverlayClick={!deleting}
      >
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent borderRadius={deleteModalSize === 'full' ? 0 : 'xl'} overflow="hidden">
          <ModalHeader bg="red.600" color="white" pr={12}>
            <HStack spacing={2}>
              <Trash2 size={18} />
              <Text>Delete game</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" isDisabled={deleting} />
          <ModalBody py={5}>
            <Box mb={4} p={3} borderRadius="md" bg="gray.50" borderWidth="1px">
              <Text fontWeight="800" fontSize="lg">{deleteTarget?.name}</Text>
              <Text fontSize="sm" color="gray.600">
                Code <b>{deleteCode}</b> · Game {deleteTarget?.gameNumber}
                {deleteTarget?.manual_draw ? ' · Manual draw' : ''}
                {deleteTarget?.is_active ? ' · Active' : ' · Inactive'}
              </Text>
              {deleteInfo && (
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Today: {deleteInfo.draws} draws ({deleteInfo.pending} without a result), {deleteInfo.tickets} tickets booked.
                </Text>
              )}
            </Box>
            <Alert status="error" borderRadius="md" mb={4} alignItems="flex-start">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                The game is removed from the panel and the player app at once. Its draws and tickets stay in the database
                without a game, so results can&apos;t be given for them until the game is restored.
                {deleteTarget?.is_active ? ' To stop play temporarily, deactivate the game instead.' : ''}
              </AlertDescription>
            </Alert>
            <Text fontSize="sm" mb={2}>
              Type the game code <b>{deleteCode}</b> to confirm.
            </Text>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={deleteCode}
              autoFocus
              autoComplete="off"
              textTransform="uppercase"
              fontFamily="mono"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && deleteConfirmed) confirmDeleteGame();
              }}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={closeDeleteModal} isDisabled={deleting}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              leftIcon={<Trash2 size={16} />}
              onClick={confirmDeleteGame}
              isDisabled={!deleteConfirmed}
              isLoading={deleting}
              loadingText="Deleting"
            >
              Delete game
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default React.memo(GameManagement);
